// "PracticeSuite demographics" — Setup > Data card (2026-10-23, docs/outreach.md).
//
// Patient outreach texts and emails a patient about a balance, and the daily
// PracticeSuite report carries no phone number or email. An admin exports the
// practice's patient demographics from PracticeSuite (CSV or Excel) and uploads it
// here; the service matches each row by account number and fills the mobile, home
// phone, email, text opt-in and language ONLY where the row has a value, never
// blanking what Atlas has and never turning a "no" to texts into a "yes"
// (services/practicesuite_demographics_service.py). Nothing here names a patient:
// the response is counts and warnings.
//
//   GET  /api/practices/<id>/practicesuite-demographics             applies, last_upload
//   POST /api/practices/<id>/practicesuite-demographics[?dry_run=1] multipart: file

import React, { useCallback, useEffect, useState } from "react";

import { formatWhen } from "./DataPage";
import "./PsDemographicsCard.css";

const API_URL = process.env.REACT_APP_API_URL || "";

export const HINT =
  "Export the practice's patient demographics from PracticeSuite with the account number, mobile and home phone, " +
  "email, text opt-in and preferred language, as CSV or Excel, and upload it here. Rows fill a blank contact field " +
  "and update a changed one; a blank cell never clears what Atlas has, and a patient who said no to texts stays no.";

export function psDemographicsUrl(practiceId, dryRun = false) {
  const base = `${API_URL}/api/practices/${practiceId}/practicesuite-demographics`;
  return dryRun ? `${base}?dry_run=1` : base;
}

// "5 rows: 4 matched (3 updated, 1 unchanged), 1 unmatched; 1 value skipped"
export function countsLine(counts) {
  if (!counts || typeof counts !== "object") return "";
  const n = (key) => Number(counts[key]) || 0;
  let line =
    `${n("rows")} row${n("rows") === 1 ? "" : "s"}: ` +
    `${n("matched")} matched (${n("updated")} updated, ${n("unchanged")} unchanged), ` +
    `${n("unmatched")} unmatched`;
  if (n("skipped_blank") > 0) line += `; ${n("skipped_blank")} value${n("skipped_blank") === 1 ? "" : "s"} skipped`;
  if (n("consent_kept_no") > 0) line += `; ${n("consent_kept_no")} kept as no to texts`;
  return line;
}

export function lastUploadLine(last) {
  if (!last) return "";
  const parts = [formatWhen(last.at)];
  if (last.by) parts.push(`by ${last.by}`);
  const line = countsLine(last);
  if (line) parts.push(line);
  return parts.join(" · ");
}

async function request(target, options, fallback) {
  const response = await fetch(target, { credentials: "include", ...options });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const err = new Error((body && body.error) || fallback);
    err.status = response.status;
    throw err;
  }
  if (!body || typeof body !== "object") throw new Error("The response could not be read. Try again.");
  return body;
}

export default function PsDemographicsCard({ practiceId }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(null); // null | "dry" | "upload"
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    if (!practiceId) return Promise.resolve(null);
    return request(psDemographicsUrl(practiceId), {}, "Could not load the PracticeSuite demographics status.");
  }, [practiceId]);

  useEffect(() => {
    if (!practiceId) return undefined;
    let cancelled = false;
    setData(null);
    setLoadError(null);
    setHidden(false);
    setFile(null);
    setResult(null);
    load()
      .then((body) => {
        if (cancelled || !body) return;
        if (body.applies !== true) {
          setHidden(true);
          return;
        }
        setData(body);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.status === 403) setHidden(true);
        else setLoadError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId, load]);

  const send = async (dryRun) => {
    if (!file || busy) return;
    setBusy(dryRun ? "dry" : "upload");
    setError(null);
    setMessage(null);
    setResult(null);
    const body = new FormData();
    body.append("file", file);
    try {
      const payload = await request(psDemographicsUrl(practiceId, dryRun), { method: "POST", body }, "The demographics could not be imported.");
      const line = countsLine(payload.counts);
      setResult(payload);
      setMessage(payload.dry_run ? `Preview: ${line}. Nothing was changed.` : `Imported: ${line}.`);
      if (!payload.dry_run) {
        setFile(null);
        load()
          .then((fresh) => {
            if (fresh && fresh.applies === true) setData(fresh);
          })
          .catch(() => {});
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  if (!practiceId || hidden) return null;
  if (!data && !loadError) return null;

  const last = data && data.last_upload;
  const warnings = (result && Array.isArray(result.warnings) && result.warnings) || [];

  return (
    <div className="data-card ps-demographics" data-testid="ps-demographics">
      <h2>PracticeSuite demographics</h2>
      <p className="data-note">{HINT}</p>
      {loadError && <p className="data-error-line">{loadError}</p>}
      {data && (
        <>
          <p className="ps-demo-last" data-testid="ps-demographics-last">
            {last ? <>Last upload {lastUploadLine(last)}</> : "No upload yet."}
          </p>
          {warnings.length > 0 && (
            <ul className="ps-demo-warnings" data-testid="ps-demographics-warnings">
              {warnings.map((w, i) => (
                <li key={i}>{String(w)}</li>
              ))}
            </ul>
          )}
          <div className="ps-demo-upload">
            <label className="ps-demo-file">
              <span>The export (.csv or .xlsx)</span>
              <input
                type="file"
                accept=".csv,.xlsx,.txt"
                data-testid="ps-demographics-file"
                disabled={!!busy}
                onChange={(e) => {
                  const chosen = e.target.files && e.target.files[0];
                  if (chosen) setFile(chosen);
                  e.target.value = "";
                }}
              />
              {file && <span className="data-filename">{file.name}</span>}
            </label>
            <button type="button" className="data-button data-button-secondary" data-testid="ps-demographics-dry-run" disabled={!!busy || !file} onClick={() => send(true)}>
              {busy === "dry" ? "Counting…" : "Dry run"}
            </button>
            <button type="button" className="data-button" data-testid="ps-demographics-upload" disabled={!!busy || !file} onClick={() => send(false)}>
              {busy === "upload" ? "Uploading…" : "Upload"}
            </button>
          </div>
          {error && (
            <div className="data-message data-message-error" data-testid="ps-demographics-error">
              <span>{error}</span>
              <button type="button" className="data-message-close" aria-label="Dismiss" onClick={() => setError(null)}>
                ×
              </button>
            </div>
          )}
          {message && (
            <div className="data-message data-message-success" data-testid="ps-demographics-message">
              <span>{message}</span>
              <button type="button" className="data-message-close" aria-label="Dismiss" onClick={() => setMessage(null)}>
                ×
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
